// src/encounter/medication-add-dialog.tsx
// Add/edit-medication form for the rail's Medications "+" popover. Ports the Flutter rail form
// (medicine_clinical_info_add_widget.dart `frequencyView()` vertical branch, fields from
// ui_data.dart:253-461) in Flutter's order:
//   1. Brand + Form + Strength * — the coded drug search (MedicationSearchWidget). A pick shows the
//      drug's name in the field and CLOSES the results; typing again reopens search and drops it.
//   2. Frequency — As needed / Every day (default) / Days interval / Specific days.
//   3. Days interval only: Every [N] day(s).
//   4. Specific days only: Mo Tu Wd Th Fr Sa Su (Mo on by default).
//   5. Not as needed: Times a day | Units per intake.
//   6. Always: Route | Unit (both optional, prefilled from the picked drug).
//   7. Not as needed: Start date (required, no default).
//   8. Not as needed: Duration of treatment + days / weeks / mo.
// Validation runs on Save with Flutter's messages (lib/medication-add-validation.ts) and renders
// inline under each field; the first failing field takes focus. Every visual value is a Signal DS
// component or token. The dialog stays presentational for the drug search (the host feeds results);
// the route/unit master lists live in MedicationRouteUnitFields so they only load while open.
import { useEffect, useRef, useState } from 'react';
import {
  DatePicker,
  HelperText,
  InlineField,
  MedicationSearchWidget,
  TextChoiceGroup,
  type MedicationResult,
} from '@scripsteam/scrips-signal-ds';
import { Calendar } from 'lucide-react';
import { AddSurface } from './add-surface';
import { MedicationRouteUnitFields } from './medication-route-unit-fields';
import { MedicationAddField } from './medication-add-field';
import {
  toMedFrequency,
  type MedicationEditPrefill,
  type MedicationFormInput,
} from './lib/to-medication-request';
import type { MedicationOption, MedRoute } from './lib/to-medication-result';
import {
  DURATION_UNITS,
  MED_FREQUENCY,
  MED_FREQUENCY_ALL,
  MED_FREQUENCY_OPTIONS,
  WEEKDAYS,
  WEEKDAY_SHORT,
  frequencyIsScheduled,
  type MedFrequency,
} from './lib/medication-frequency';
import {
  firstInvalidField,
  validateMedicationAdd,
  type MedicationAddField as FieldName,
} from './lib/medication-add-validation';
import { DrugInteractionWarning } from './drug-interaction-warning';
import type { PatientDrugInteraction } from '@/types/patient-chart-api';

export type { MedicationOption, MedicationEditPrefill };

export interface MedicationAddDialogProps {
  open: boolean;
  /** Search box value (host-controlled, fed to useMedicationSearch). */
  searchValue: string;
  onSearchChange: (next: string) => void;
  /** Coded results from the medication search (carry the coding system). */
  results: MedicationOption[];
  /** When set, the dialog is in EDIT mode: fixed medication, prefilled fields. */
  editing?: MedicationEditPrefill | null;
  saving?: boolean;
  /**
   * Save the entry. A host that returns a promise gets the honest flow: the add form is cleared
   * (and the search text reset through `onSearchChange('')`) only once it RESOLVES; a rejection
   * keeps everything the clinician typed. A host that returns nothing gives no completion signal,
   * so the add form is cleared at once, as before.
   */
  onSave: (input: MedicationFormInput) => void | Promise<unknown>;
  onClose: () => void;

  // ── Inline drug-interaction check (PROD parity — Flutter getDrugInteractions) ────────────────
  // Presentational, like the search results: the host runs `useDrugInteractions` for the selected
  // med and feeds the alerts back here. The dialog reports the picked med via `onMedicationSelect`
  // so the host can key the check. All optional — a host that doesn't wire them shows no warning.
  /** Alerts for the currently-selected med (from the host's useDrugInteractions). */
  interactions?: PatientDrugInteraction[];
  /** True while the interaction check is in flight. */
  interactionsLoading?: boolean;
  /**
   * The interaction check FAILED (400/500/network) — mirrors medication-dose-dialog's wiring.
   * Without this, a failed check just renders nothing (interactions stays `undefined`, loading
   * ends) and the dialog silently allows saving with NO indication the safety check didn't run.
   * Forwarded straight to DrugInteractionWarning's `error` so the add/edit flows get the same
   * explicit failure + Retry card the dose dialog already shows.
   */
  interactionsError?: boolean;
  /** Re-run the interaction check after a failure. Forwarded to DrugInteractionWarning's `onRetry`. */
  onInteractionsRetry?: () => void;
  /** Fired when the effective medication changes (picked / edit-prefill / cleared) so the host can
   *  run the check. `null` when no med is selected. */
  onMedicationSelect?: (med: { code: string; display: string; system: string } | null) => void;
}

/** Duration unit pills, Flutter's copy (ui_data.dart:443-457) over the wire ids day / wk / mo. */
const DURATION_UNIT_LABEL: Record<(typeof DURATION_UNITS)[number], string> = { day: 'days', wk: 'weeks', mo: 'mo' };
const DURATION_UNIT_OPTIONS: { value: string; label: string; 'aria-label'?: string }[] = DURATION_UNITS.map((u) => ({
  value: u,
  label: DURATION_UNIT_LABEL[u],
  // "mo" is Flutter's visible copy; a screen reader hears the word.
  ...(u === 'mo' ? { 'aria-label': 'months' } : {}),
}));

/** Flutter's date bounds for this field: 1 Jan 1900 … today + 6000 days (ui_data.dart:413-416). */
const MIN_START = new Date(1900, 0, 1);
function maxStart(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 6000);
  return d;
}

// The Flutter form grammar (PROD-3086): no field labels and no boxes, a hint, a value and a unit
// on one line. Every root is `min-w-0`, so no control's intrinsic width can push the body wider than
// the popover, and nothing scrolls sideways. 16px between rows (the DS body rhythm) and 16px under
// the separator, which the rail container strips from the DS body (`bodyClassName="... pt-0"`) so
// each form owns its top inset. The 24px under the last row is the DS body's own (measured).
const BODY = 'flex flex-col gap-4 min-w-0 pt-4';
const PAIR = 'flex min-w-0 items-start gap-6';
const INLINE_ROW = 'flex items-start gap-1 min-w-0';
const INLINE_TEXT = 'text-size-sm text-text-secondary';
const DURATION_ROW = 'flex min-w-0 items-start';
const DRUG_NAME = 'm-0 text-size-sm text-text-primary';
// Flutter's date box is 125 wide (medicine...:636); `w-32` (128) is the nearest step on the scale.
const DATE_TRIGGER =
  'flex w-32 items-center justify-between gap-1 rounded-sm border-0 bg-transparent p-0 font-sans text-size-sm ' +
  'cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary';

/** Mo Tu Wd Th Fr Sa Su over the full weekday names the wire carries, each named in full. */
const WEEKDAY_OPTIONS = WEEKDAYS.map((day) => ({
  value: day,
  label: WEEKDAY_SHORT[day] ?? day.slice(0, 2),
  'aria-label': day,
}));

/** Project a coded option to the DS MedicationResult display subset {id,name,code,strength}. */
function toResult(o: MedicationOption): MedicationResult {
  return { id: o.id, name: o.name, code: o.code, strength: o.strength };
}

/** `YYYY-MM-DD` (local) → Date at local midnight. */
function dayToDate(day: string | undefined): Date | undefined {
  const m = day ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
}

/** Date → `YYYY-MM-DD` in local time (no UTC day-shift). */
function dateToDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** `dd/MM/yyyy`, the Flutter field's format (field_and_label.dart:355). */
function formatDdMmYyyy(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function numText(n: number | undefined): string {
  return typeof n === 'number' ? String(n) : '';
}

export function MedicationAddDialog({
  open, searchValue, onSearchChange, results, editing, saving, onSave, onClose,
  interactions, interactionsLoading, interactionsError, onInteractionsRetry, onMedicationSelect,
}: MedicationAddDialogProps) {
  const isEditing = !!editing;
  const bodyRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<MedicationOption | undefined>();
  // Add opens on Flutter's default (Every day); an edit opens on the record's frequency, or on
  // NOTHING when the record holds a legacy free-text value ("BID") the form cannot represent.
  const [frequency, setFrequency] = useState<MedFrequency | null>(() =>
    editing ? toMedFrequency(editing.frequency) : MED_FREQUENCY.everyDay,
  );
  const [daysInterval, setDaysInterval] = useState(() => numText(editing?.daysInterval));
  const [specificDays, setSpecificDays] = useState<string[]>(() =>
    editing ? [...(editing.specificDays ?? [])] : ['Monday'],
  );
  const [timesADay, setTimesADay] = useState(() => numText(editing?.timesADay));
  const [unitsPerIntake, setUnitsPerIntake] = useState(() => editing?.doseCount ?? '');
  const [startDate, setStartDate] = useState<Date | undefined>(() => dayToDate(editing?.startDate));
  const [duration, setDuration] = useState(() => editing?.duration ?? '');
  const [durationType, setDurationType] = useState(() => editing?.durationType || DURATION_UNITS[0]);
  const [route, setRoute] = useState<MedRoute | null>(() => editing?.route ?? null);
  const [unit, setUnit] = useState(() => editing?.unitOfMeasurement ?? '');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});

  // Report the effective medication to the host so it can run the drug-interaction check (Flutter
  // getDrugInteractions fires on select / on open in edit). `null` clears it when closed or unpicked.
  useEffect(() => {
    if (!onMedicationSelect) return;
    if (!open) {
      onMedicationSelect(null);
      return;
    }
    const picked = isEditing
      ? editing!.medication
      : selected
        ? { code: selected.code, display: selected.name, system: selected.system }
        : null;
    onMedicationSelect(picked ? { code: picked.code, display: picked.display, system: picked.system } : null);
  }, [open, isEditing, editing, selected, onMedicationSelect]);

  if (!open) return null;

  // Edit mode: the medication is fixed (header below); Add mode: picked from the search.
  const med = isEditing
    ? editing!.medication
    : selected && { code: selected.code, display: selected.name, system: selected.system };
  const scheduled = frequency ? frequencyIsScheduled(frequency) : false;
  // Drug results are on screen: the only content in this form that can arrive under the caret.
  const searching = !isEditing && !selected && results.length !== 0;

  function clearError(field: FieldName) {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function handleResultClick(r: MedicationResult) {
    const full = results.find((o) => o.id === r.id);
    if (!full) return;
    setSelected(full);
    // The field shows the drug's name, and with a pick held the results list is closed.
    onSearchChange(full.name);
    // Prefill route + unit from the drug (Flutter setRouteData): a single route is taken as is;
    // several leave the choice to the clinician.
    setRoute(full.route?.length === 1 ? full.route[0] : null);
    if (full.unitOfMeasurement) setUnit(full.unitOfMeasurement);
    clearError('medication');
  }

  function handleSearchChange(next: string) {
    // Typing again reopens the search and drops the pick.
    if (selected) setSelected(undefined);
    onSearchChange(next);
  }

  function resetForm() {
    setSelected(undefined);
    setFrequency(MED_FREQUENCY.everyDay);
    setDaysInterval('');
    setSpecificDays(['Monday']);
    setTimesADay('');
    setUnitsPerIntake('');
    setStartDate(undefined);
    setDuration('');
    setDurationType(DURATION_UNITS[0]);
    setRoute(null);
    setUnit('');
    setErrors({});
  }

  function handleClose() {
    // The rail keeps one add instance mounted with `open={false}`; start it clean next time.
    if (!isEditing) resetForm();
    onClose();
  }

  function focusField(field: FieldName) {
    const box = bodyRef.current?.querySelector<HTMLElement>(`[data-field="${field}"]`);
    if (!box) return;
    box.scrollIntoView?.({ block: 'nearest' });
    box.querySelector<HTMLElement>('input:not([readonly]), button, [tabindex]')?.focus();
  }

  function handleSave() {
    const found = validateMedicationAdd({
      hasMedication: !!med,
      frequency,
      daysInterval,
      specificDays,
      timesADay,
      unitsPerIntake,
      startDate: startDate ? dateToDay(startDate) : undefined,
      duration,
    });
    setErrors(found);
    const first = firstInvalidField(found);
    if (first || !med) {
      if (first) focusField(first);
      return;
    }
    if (!frequency) {
      // An edited record whose frequency the form can't model, not re-picked: its schedule goes
      // back exactly as fetched (only route and unit changed), never re-classified by a default.
      void onSave({
        medication: med,
        frequency: editing?.frequency,
        doseCount: editing?.doseCount,
        duration: editing?.duration,
        durationType: editing?.durationType,
        route: route ? { code: route.code, display: route.display, system: route.system ?? '' } : undefined,
        unitOfMeasurement: unit.trim() || undefined,
      });
      return;
    }
    afterSave(onSave({
      medication: med,
      frequency,
      ...(scheduled
        ? {
            doseCount: unitsPerIntake.trim(),
            timesADay: Number(timesADay),
            startDate: startDate ? dateToDay(startDate) : undefined,
            duration: duration.trim(),
            durationType,
            ...(frequency === MED_FREQUENCY.daysInterval ? { daysInterval: Number(daysInterval) } : {}),
            ...(frequency === MED_FREQUENCY.specificDays
              ? { specificDays: WEEKDAYS.filter((d) => specificDays.includes(d)) }
              : {}),
          }
        : {}),
      route: route ? { code: route.code, display: route.display, system: route.system ?? '' } : undefined,
      unitOfMeasurement: unit.trim() || undefined,
    }));
  }

  /** Clear the add form after a save — only once it succeeded, when the host says so. */
  function afterSave(result: void | Promise<unknown>) {
    if (isEditing) return;
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      (result as Promise<unknown>).then(
        () => {
          resetForm();
          onSearchChange('');
        },
        // Not saved: keep every value so the clinician can retry; the host reports the failure.
        () => undefined,
      );
      return;
    }
    resetForm();
  }

  return (
    <AddSurface
      open={open}
      onClose={handleClose}
      title={isEditing ? 'Edit medication' : 'Medication Details'}
      // The Flutter production popup chrome (DS `variant="detail"`): 36px band, 20px glyphs,
      // brand-depth title, and the DS's own 336 width (the Flutter menu's 6x56 cap, which the
      // form's requested 400 is clamped to), so no width is passed from here (PROD-3086).
      popoverVariant="detail"
      // Hug the form. The body-height RESERVE exists for content that arrives under the caret,
      // and here that is only the drug results while the search is open. Once a drug is picked
      // (or on edit) nothing more can arrive, so the box hugs the form as Flutter's does.
      reserve={searching}
      // ACTIVE without a drug, as in Flutter (owner's ruling, 2026-10-07): the save-time validation
      // names the missing medication ("Invalid medication..."), which a disabled checkmark would
      // never let the user see.
      commit={{ run: handleSave, label: 'Save medication', text: 'Save', disabled: false, busy: saving }}
    >
      <div ref={bodyRef} className={`font-sans ${BODY}`} data-slot="med-add-form">
        <MedicationAddField name="medication" error={errors.medication}>
          {isEditing ? (
            // Edit: the drug is fixed, so it reads as the value the search field would hold.
            <p className={DRUG_NAME} data-slot="med-add-drug">{editing!.medication.display}</p>
          ) : (
            <MedicationSearchWidget
              variant="inline"
              // Named for what it holds, so a reader does not announce the placeholder as the name.
              aria-label="Medication (brand, form and strength)"
              required
              value={searchValue}
              onChange={handleSearchChange}
              results={selected ? [] : results.map(toResult)}
              onResultClick={handleResultClick}
              placeholder="Brand + Form + Strength *"
            />
          )}
        </MedicationAddField>

        {/* Inline drug-interaction warning (Flutter DrugInteractionInfoWidget) — the host feeds the
            alerts for the selected med; nothing when none (no all-clear, PROD-2721). */}
        {med && (
          <DrugInteractionWarning
            interactions={interactions}
            loading={interactionsLoading}
            error={interactionsError}
            onRetry={onInteractionsRetry}
            triggerCode={med.code}
          />
        )}

        <div data-field="frequency" className="min-w-0">
          {/* Plain-text choices that WRAP, never a scrolling pill row (PROD-3086). */}
          <TextChoiceGroup<MedFrequency>
            aria-label="Frequency"
            // `Now` is hidden for new entries, but a record saved with it keeps its choice visible.
            options={[...(frequency === MED_FREQUENCY.now ? MED_FREQUENCY_ALL : MED_FREQUENCY_OPTIONS)]}
            value={frequency}
            onChange={(v) => {
              setFrequency(v);
              setErrors((prev) => (prev.medication ? { medication: prev.medication } : {}));
            }}
          />
          {!frequency && (
            <HelperText>
              {editing?.frequency?.trim()
                ? `Saved as “${editing.frequency.trim()}”. Pick a frequency to change the schedule.`
                : 'No frequency on record. Pick one to set the schedule.'}
            </HelperText>
          )}
        </div>

        {frequency === MED_FREQUENCY.daysInterval && (
          <MedicationAddField name="daysInterval">
            <div className={INLINE_ROW}>
              <span className={INLINE_TEXT} data-slot="med-add-inline">Every</span>
              <InlineField
                aria-label="Every N days"
                placeholder="number"
                suffix="day(s)"
                width="number"
                inputMode="numeric"
                required
                value={daysInterval}
                onChange={(v) => {
                  setDaysInterval(v);
                  clearError('daysInterval');
                }}
                error={errors.daysInterval}
              />
            </div>
          </MedicationAddField>
        )}

        {frequency === MED_FREQUENCY.specificDays && (
          <MedicationAddField name="specificDays" error={errors.specificDays}>
            <TextChoiceGroup
              multiple
              aria-label="Specific days"
              options={WEEKDAY_OPTIONS}
              value={specificDays}
              onChange={(days) => {
                setSpecificDays(days);
                clearError('specificDays');
              }}
            />
          </MedicationAddField>
        )}

        {scheduled && (
          <div className={PAIR}>
            <MedicationAddField name="timesADay">
              <InlineField
                aria-label="Times a day"
                placeholder="Number"
                suffix="times a day"
                width="number"
                inputMode="numeric"
                required
                value={timesADay}
                onChange={(v) => {
                  setTimesADay(v);
                  clearError('timesADay');
                }}
                error={errors.timesADay}
              />
            </MedicationAddField>
            <MedicationAddField name="unitsPerIntake">
              <InlineField
                aria-label="Units per intake"
                placeholder="Number"
                suffix="units per intake"
                width="number"
                inputMode="decimal"
                required
                value={unitsPerIntake}
                onChange={(v) => {
                  setUnitsPerIntake(v);
                  clearError('unitsPerIntake');
                }}
                error={errors.unitsPerIntake}
              />
            </MedicationAddField>
          </div>
        )}

        <MedicationRouteUnitFields
          drugRoutes={isEditing ? undefined : selected?.route}
          route={route}
          onRouteChange={setRoute}
          unit={unit}
          onUnitChange={setUnit}
        />

        {scheduled && (
          <MedicationAddField name="startDate">
            {/* The DS DatePicker behind an inline trigger: the hint and a 16px calendar glyph, no
                box (Flutter FieldAndLabel `compatDateView`, dd/MM/yyyy), as the DS story does it. */}
            <DatePicker
              value={startDate}
              onChange={(d) => {
                setStartDate(d);
                clearError('startDate');
              }}
              minDate={MIN_START}
              maxDate={maxStart()}
              dateFormat="dd/MM/yyyy"
              required
              error={errors.startDate}
              trigger={
                <button
                  type="button"
                  aria-label={startDate ? `Start date, ${formatDdMmYyyy(startDate)}` : 'Start date'}
                  aria-invalid={errors.startDate ? true : undefined}
                  className={DATE_TRIGGER} data-slot="med-add-date"
                >
                  <span className={startDate ? 'text-text-primary' : 'text-text-hint'}>
                    {startDate ? formatDdMmYyyy(startDate) : 'dd/mm/yyyy'}
                  </span>
                  <Calendar className="size-4 text-text-secondary" aria-hidden />
                </button>
              }
            />
          </MedicationAddField>
        )}

        {scheduled && (
          <MedicationAddField name="duration">
            {/* The field fills; the unit choices sit 4px after it and 8px from the edge. */}
            <div className={DURATION_ROW}>
              <InlineField
                aria-label="Duration of treatment"
                placeholder="Duration of treatment *"
                inputMode="numeric"
                required
                value={duration}
                onChange={(v) => {
                  setDuration(v);
                  clearError('duration');
                }}
                error={errors.duration}
              />
              <TextChoiceGroup
                aria-label="Duration unit"
                className="ms-1 me-2 shrink-0 flex-nowrap"
                options={DURATION_UNIT_OPTIONS}
                value={durationType}
                onChange={setDurationType}
              />
            </div>
          </MedicationAddField>
        )}
      </div>
    </AddSurface>
  );
}
